import { useCallback, useMemo, useState } from 'react'
import { View, Text, Pressable, Platform, ActivityIndicator, TextInput } from 'react-native'
import { useRouter, type Href } from 'expo-router'
import { WEB_HOVER_TRANSITION_STYLE } from '../../../shared/constants/animations'
import { useB2bBillingActions } from '../../hooks/useB2bBillingActions'
import { useAccountAdminCopy } from '../../hooks/useAccountAdminCopy'
import {
  buildOrgSeatTierUpgradeDialogMessage,
} from '../../constants/billingPlans.constants'
import { buildOrgOneTimePaidAccessSummary } from '../../helpers/orgBillingDisclosure.helper'
import { formatOrgPlanTierLabel } from '../../helpers/billingDisplay.helper'
import { ENTERPRISE_CONTACT_PATH } from '../../../packs/constants/enterpriseContact.constants'
import { usePricingI18n } from '../../../packs/hooks/usePricingI18n'
import { Check } from 'lucide-react-native'
import { SeatBucketPicker } from '../../../packs/components/SeatBucketPicker/SeatBucketPicker'
import { useDialog } from '../../../shared/hooks/useDialog'
import { useApiClient, extractErrorMessage } from '../../../../helpers/apiClient.helper'
import { API_ENDPOINTS } from '../../../../constants/api'
import { getAccountBillingTabStyles } from './AccountBillingTab.styles'
import { ACCOUNT_BILLING_TAB_COPY } from './AccountBillingTab.constants'
import {
  sanitizeContactLeadFormInput,
  validateContactLeadPayload,
} from '../../helpers/orgFormValidation.helper'
import type { Theme } from '../../../theme/getTheme'
import type { B2bAccountDto } from '../../types/b2bAccount.types'

interface AccountBillingTabProps {
  accountId: string | null
  account: B2bAccountDto | null
  theme: Theme
  /** After B2B Stripe return: sync + refresh in flight (see AccountAdminScreen). */
  stripeReturnSyncing?: boolean
  /** Organization display name for pre-filling the custom plan contact form. */
  orgName?: string
}

export function AccountBillingTab({
  accountId,
  account,
  theme,
  stripeReturnSyncing = false,
  orgName,
}: AccountBillingTabProps) {
  const copy = useAccountAdminCopy()
  const { seatBuckets, planCards } = usePricingI18n()
  const styles = useMemo(() => getAccountBillingTabStyles(theme), [theme])
  const { showDialog } = useDialog()
  const apiClient = useApiClient()
  const { startCheckout, busy: busyOrg, stripeConfigured } =
    useB2bBillingActions(accountId, account ?? undefined)
  const router = useRouter()

  const hasOrganization = account != null && accountId != null
  const hasOrgSubscription = hasOrganization && Boolean(account.stripeSubscriptionStatus?.trim())
  const isOrgFree = !hasOrganization || account.planTier === 'FREE' || account.planTier === 'STARTER'

  const isManualBilling = hasOrganization && account.billingProvider === 'MANUAL'
  const isStripeCustom = hasOrganization && account.billingProvider === 'STRIPE_CUSTOM'
  /** STRIPE_CUSTOM orgs are "paid" when a subscription is active OR a one-time payment intent was recorded. */
  const isStripeCustomPaid =
    isStripeCustom && (hasOrgSubscription || Boolean(account.stripePaymentIntentId?.trim()))

  /** One-time Business plan: paid tier but no recurring Stripe subscription. */
  const isOneTimeBusiness =
    hasOrganization && account.planTier === 'BUSINESS' && !hasOrgSubscription

  const currentBucket = useMemo(
    () =>
      isOneTimeBusiness
        ? (seatBuckets.find((b) => b.maxSeats === account!.maxSeats) ?? seatBuckets[0])
        : null,
    [isOneTimeBusiness, account, seatBuckets],
  )
  const upgradableBuckets = useMemo(
    () =>
      currentBucket
        ? seatBuckets.filter((b) => b.maxSeats > currentBucket.maxSeats)
        : [],
    [currentBucket, seatBuckets],
  )
  const [selectedUpgradeBucket, setSelectedUpgradeBucket] = useState<string | null>(null)
  const selectedBucketKey =
    selectedUpgradeBucket ?? (upgradableBuckets[0]?.key ?? currentBucket?.key ?? '')

  const currentSeatTierDisplay = useMemo(
    () =>
      currentBucket != null
        ? `${currentBucket.tagline} — ${currentBucket.priceLabel}`
        : '',
    [currentBucket],
  )

  const selectedTargetBucket = useMemo(
    () => upgradableBuckets.find((b) => b.key === selectedBucketKey) ?? null,
    [upgradableBuckets, selectedBucketKey],
  )

  const handleUpgrade = useCallback(async () => {
    if (currentBucket == null || selectedTargetBucket == null) return
    const confirmed = await showDialog({
      title: copy.billing.SEAT_TIER_UPGRADE_DIALOG_TITLE,
      message: buildOrgSeatTierUpgradeDialogMessage(currentBucket, selectedTargetBucket, copy.billing),
      type: 'default',
      confirmText: copy.billing.SEAT_TIER_UPGRADE_DIALOG_CONFIRM,
      cancelText: copy.billing.SEAT_TIER_UPGRADE_DIALOG_CANCEL,
    })
    if (!confirmed) return
    void startCheckout('BUSINESS', selectedBucketKey)
  }, [
    copy.billing,
    currentBucket,
    selectedTargetBucket,
    selectedBucketKey,
    showDialog,
    startCheckout,
  ])

  const enterpriseAgreedPriceLine = useMemo(() => {
    if (!account?.stripeNegotiatedPriceDisplay) return null
    const { main, sub } = account.stripeNegotiatedPriceDisplay
    const cadence = typeof sub === 'string' ? sub.trim() : ''
    return cadence.length > 0 ? `${main} / ${cadence}` : main
  }, [account?.stripeNegotiatedPriceDisplay])

  const showEnterpriseAgreedPrice =
    hasOrganization && enterpriseAgreedPriceLine != null && !hasOrgSubscription

  const showSeatTierCompareWithUpgrade =
    isOneTimeBusiness &&
    currentBucket != null &&
    upgradableBuckets.length > 0

  // ─── Plan-picker state (FREE orgs) ───────────────────────────────────────────
  const [pickerBucketKey, setPickerBucketKey] = useState<string>(
    seatBuckets[0]?.key ?? '',
  )
  const [showCustomForm, setShowCustomForm] = useState(false)
  const [customName, setCustomName] = useState('')
  const [customEmail, setCustomEmail] = useState('')
  const [customMessage, setCustomMessage] = useState('')
  const [customSubmitting, setCustomSubmitting] = useState(false)
  const [customError, setCustomError] = useState<string | null>(null)
  const [customSent, setCustomSent] = useState(false)

  const handleBusinessCheckout = useCallback(() => {
    void startCheckout('BUSINESS', pickerBucketKey)
  }, [startCheckout, pickerBucketKey])

  const handleCustomSubmit = useCallback(async () => {
    if (!customName.trim() || !customEmail.trim()) {
      setCustomError(ACCOUNT_BILLING_TAB_COPY.CUSTOM_FORM_NAME_EMAIL_REQUIRED)
      return
    }
    const effectiveOrgName = orgName ?? account?.name ?? ''
    const validation = validateContactLeadPayload({
      orgName: effectiveOrgName,
      contactName: customName,
      contactEmail: customEmail,
      message: customMessage,
    })
    if (!validation.ok || !validation.value) {
      setCustomError(validation.error ?? ACCOUNT_BILLING_TAB_COPY.CUSTOM_FORM_NAME_EMAIL_REQUIRED)
      return
    }
    setCustomError(null)
    setCustomSubmitting(true)
    try {
      await apiClient(API_ENDPOINTS.accounts.contactLead, {
        method: 'POST',
        data: {
          orgName: validation.value.orgName,
          contactName: validation.value.contactName,
          contactEmail: validation.value.contactEmail,
          message: validation.value.message || null,
        },
      })
      setCustomSent(true)
    } catch (e) {
      setCustomError(
        extractErrorMessage(e, ACCOUNT_BILLING_TAB_COPY.CUSTOM_FORM_SEND_FAILURE),
      )
    } finally {
      setCustomSubmitting(false)
    }
  }, [customName, customEmail, customMessage, orgName, account, apiClient])

  // ─── STRIPE_CUSTOM: pre-payment activation card ─────────────────────────────
  if (isStripeCustom && !isStripeCustomPaid) {
    const productLabel = account!.manualProductLabel ?? null
    const features = planCards.find((p) => p.tier === 'ENTERPRISE')?.features ?? []

    return (
      <View style={styles.cardWrap}>
        <View style={styles.container}>
          <View style={styles.customEnterpriseHeader}>
            <View style={styles.customEnterpriseBadge}>
              <Text style={styles.customEnterpriseBadgeText}>Custom Enterprise Plan</Text>
            </View>
            {productLabel ? (
              <Text style={styles.customEnterpriseProduct}>{productLabel}</Text>
            ) : null}
            {enterpriseAgreedPriceLine ? (
              <Text style={styles.customEnterprisePrice}>{enterpriseAgreedPriceLine}</Text>
            ) : (
              <Text style={styles.customEnterprisePricePlaceholder}>Custom pricing</Text>
            )}
            <Text style={styles.customEnterpriseSeats}>
              Up to {account!.maxSeats} seats
            </Text>
          </View>

          {features.length > 0 ? (
            <View style={styles.planCardFeatureList}>
              {features.map((f) => (
                <View key={f.label} style={styles.planCardFeatureRow}>
                  <Check size={13} color={theme.primary} strokeWidth={2.5} />
                  <Text style={styles.planCardFeatureText}>{f.label}</Text>
                </View>
              ))}
            </View>
          ) : null}

          <View style={styles.stripeCustomActions}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Complete payment"
              disabled={busyOrg}
              onPress={() => startCheckout('ENTERPRISE')}
              style={({ pressed, hovered }) => [
                styles.stripeCustomPayBtn,
                busyOrg && styles.stripeCustomPayBtnDisabled,
                pressed && styles.stripeCustomPayBtnPressed,
                hovered && Platform.OS === 'web' && styles.stripeCustomPayBtnHover,
              ]}
            >
              {busyOrg ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <Text style={styles.stripeCustomPayBtnText}>Complete payment</Text>
              )}
            </Pressable>
            <Pressable
              accessibilityRole="link"
              accessibilityLabel="Contact us to modify your plan"
              onPress={() => router.push(ENTERPRISE_CONTACT_PATH as Href)}
              style={({ pressed, hovered }) => [
                styles.manualContactLink,
                Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                pressed && styles.manualContactLinkPressed,
                hovered && Platform.OS === 'web' && styles.manualContactLinkHover,
              ]}
            >
              <Text style={styles.manualContactLinkText}>Contact us to modify your plan</Text>
            </Pressable>
          </View>
        </View>
      </View>
    )
  }

  // ─── MANUAL billing: plan summary only (payment details on Billing & invoice) ─
  if (isManualBilling) {
    const productLabel = account!.manualProductLabel ?? null

    return (
      <View style={styles.cardWrap}>
        <View style={styles.container}>
          {productLabel ? (
            <View style={styles.planSummary}>
              <Text style={styles.planLabel}>Product</Text>
              <Text style={styles.planValue}>{productLabel}</Text>
            </View>
          ) : null}

          <View style={styles.planSummary}>
            <Text style={styles.planLabel}>{copy.billing.ORGANIZATION_PLAN_LABEL}</Text>
            <Text style={styles.planValue}>
              {formatOrgPlanTierLabel(account!.planTier, copy.billing)}
            </Text>
            <Text style={styles.orgPaidAccessSummary}>
              {buildOrgOneTimePaidAccessSummary(account!.maxSeats)}
            </Text>
          </View>

          <Pressable
            accessibilityRole="link"
            accessibilityLabel={copy.billing.NEED_CUSTOM_PLAN_A11Y}
            onPress={() => router.push(ENTERPRISE_CONTACT_PATH as Href)}
            style={({ pressed, hovered }) => [
              styles.comparePlansLink,
              Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              pressed && styles.comparePlansLinkPressed,
              hovered && Platform.OS === 'web' && styles.comparePlansLinkHover,
            ]}
          >
            <Text style={styles.comparePlansLinkText}>{copy.billing.NEED_CUSTOM_PLAN}</Text>
          </Pressable>
        </View>
      </View>
    )
  }

  // ─── FREE: plan picker view ───────────────────────────────────────────────────
  if (isOrgFree) {
    return (
      <View style={styles.cardWrap}>
        <View style={styles.container}>
          <Text style={styles.planPickerHeading}>{copy.billing.PLAN_PICKER_HEADING}</Text>
          <Text style={styles.planPickerSubheading}>{copy.billing.PLAN_PICKER_SUBHEADING}</Text>

          {!stripeConfigured ? (
            <View style={styles.unconfiguredBanner}>
              <Text style={styles.unconfiguredText}>{copy.billing.STRIPE_NOT_CONFIGURED}</Text>
            </View>
          ) : null}

          {/* Business plan card */}
          <View style={styles.planCard}>
            <Text style={styles.planCardName}>{copy.billing.PLAN_BUSINESS_NAME}</Text>
            <Text style={styles.planCardPrice}>{copy.billing.PLAN_BUSINESS_PRICE}</Text>
            <Text style={styles.planCardTagline}>{copy.billing.PLAN_BUSINESS_TAGLINE}</Text>
            <View style={styles.planCardFeatureList}>
              {(planCards.find((p) => p.tier === 'BUSINESS')?.features ?? []).map((f) => (
                <View key={f.label} style={styles.planCardFeatureRow}>
                  <Check size={13} color={theme.primary} strokeWidth={2.5} />
                  <Text style={styles.planCardFeatureText}>{f.label}</Text>
                </View>
              ))}
            </View>
            <SeatBucketPicker
              inlineRow
              buckets={seatBuckets}
              selectedKey={pickerBucketKey}
              onSelect={setPickerBucketKey}
              disabled={busyOrg || stripeReturnSyncing || !stripeConfigured}
            />
            <Text style={styles.orgBillingDisclosure}>{copy.billing.ORG_BILLING_DISCLOSURE}</Text>
            <Pressable
              accessibilityRole="button"
              disabled={busyOrg || stripeReturnSyncing || !stripeConfigured}
              onPress={handleBusinessCheckout}
              style={({ pressed, hovered }) => [
                styles.planCardCta,
                Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                pressed && styles.planCardCtaPressed,
                hovered && Platform.OS === 'web' && styles.planCardCtaHover,
                (busyOrg || stripeReturnSyncing || !stripeConfigured) &&
                  styles.planCardCtaDisabled,
              ]}
            >
              {busyOrg ? (
                <ActivityIndicator size="small" color={theme.white} />
              ) : (
                <Text style={styles.planCardCtaText}>{copy.billing.PLAN_BUSINESS_CTA}</Text>
              )}
            </Pressable>
          </View>

          {/* Enterprise / Custom card */}
          <View style={styles.planCard}>
            <Text style={styles.planCardName}>{copy.billing.PLAN_ENTERPRISE_NAME}</Text>
            <Text style={styles.planCardPrice}>{copy.billing.PLAN_ENTERPRISE_PRICE}</Text>
            <Text style={styles.planCardTagline}>{copy.billing.PLAN_ENTERPRISE_TAGLINE}</Text>
            <View style={styles.planCardFeatureList}>
              {(planCards.find((p) => p.tier === 'ENTERPRISE')?.features ?? []).map((f) => (
                <View key={f.label} style={styles.planCardFeatureRow}>
                  <Check size={13} color={theme.primary} strokeWidth={2.5} />
                  <Text style={styles.planCardFeatureText}>{f.label}</Text>
                </View>
              ))}
            </View>

            {customSent ? (
              <Text style={styles.customFormSuccess}>{copy.billing.CUSTOM_FORM_SENT}</Text>
            ) : showCustomForm ? (
              <View style={styles.customFormContainer}>
                <Text style={styles.customFormLabel}>{copy.billing.CUSTOM_FORM_NAME_LABEL}</Text>
                <TextInput
                  value={customName}
                  onChangeText={(text) => {
                    const cleaned = sanitizeContactLeadFormInput({
                      orgName: orgName ?? account?.name ?? '',
                      contactName: text,
                      contactEmail: customEmail,
                      message: customMessage,
                    })
                    setCustomName(cleaned.contactName)
                  }}
                  placeholder={copy.billing.CUSTOM_FORM_NAME_PLACEHOLDER}
                  placeholderTextColor={theme.gray[400]}
                  style={styles.customFormInput}
                  autoCapitalize="words"
                />
                <Text style={styles.customFormLabel}>{copy.billing.CUSTOM_FORM_EMAIL_LABEL}</Text>
                <TextInput
                  value={customEmail}
                  onChangeText={(text) => {
                    const cleaned = sanitizeContactLeadFormInput({
                      orgName: orgName ?? account?.name ?? '',
                      contactName: customName,
                      contactEmail: text,
                      message: customMessage,
                    })
                    setCustomEmail(cleaned.contactEmail)
                  }}
                  placeholder={copy.billing.CUSTOM_FORM_EMAIL_PLACEHOLDER}
                  placeholderTextColor={theme.gray[400]}
                  style={styles.customFormInput}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  autoCorrect={false}
                />
                <Text style={styles.customFormLabel}>
                  {copy.billing.CUSTOM_FORM_MESSAGE_LABEL}
                </Text>
                <TextInput
                  value={customMessage}
                  onChangeText={(text) => {
                    const cleaned = sanitizeContactLeadFormInput({
                      orgName: orgName ?? account?.name ?? '',
                      contactName: customName,
                      contactEmail: customEmail,
                      message: text,
                    })
                    setCustomMessage(cleaned.message)
                  }}
                  placeholder={copy.billing.CUSTOM_FORM_MESSAGE_PLACEHOLDER}
                  placeholderTextColor={theme.gray[400]}
                  style={styles.customFormTextArea}
                  multiline
                />
                {customError ? (
                  <Text style={styles.customFormError}>{customError}</Text>
                ) : null}
                <Pressable
                  accessibilityRole="button"
                  disabled={customSubmitting}
                  onPress={() => void handleCustomSubmit()}
                  style={({ pressed, hovered }) => [
                    styles.planCardCta,
                    Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                    pressed && styles.planCardCtaPressed,
                    hovered && Platform.OS === 'web' && styles.planCardCtaHover,
                    customSubmitting && styles.planCardCtaDisabled,
                  ]}
                >
                  {customSubmitting ? (
                    <ActivityIndicator size="small" color={theme.white} />
                  ) : (
                    <Text style={styles.planCardCtaText}>{copy.billing.CUSTOM_FORM_SUBMIT}</Text>
                  )}
                </Pressable>
              </View>
            ) : (
              <Pressable
                accessibilityRole="button"
                onPress={() => setShowCustomForm(true)}
                style={({ pressed, hovered }) => [
                  styles.planCardCtaOutline,
                  Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                  pressed && styles.planCardCtaPressed,
                  hovered && Platform.OS === 'web' && styles.planCardCtaHover,
                ]}
              >
                <Text style={styles.planCardCtaOutlineText}>
                  {copy.billing.PLAN_ENTERPRISE_CTA}
                </Text>
              </Pressable>
            )}
          </View>
        </View>
      </View>
    )
  }

  // ─── PAID: existing org billing summary ──────────────────────────────────────
  return (
    <View style={styles.cardWrap}>
      <View style={styles.container}>
        {hasOrganization ? (
          <View style={styles.planSummary}>
            <Text style={styles.planLabel}>{copy.billing.ORGANIZATION_PLAN_LABEL}</Text>
            <Text style={styles.planValue}>{formatOrgPlanTierLabel(account.planTier, copy.billing)}</Text>
            {isOneTimeBusiness ? (
              <Text style={styles.orgPaidAccessSummary}>
                {buildOrgOneTimePaidAccessSummary(account.maxSeats)}
              </Text>
            ) : null}
          </View>
        ) : null}

        {showEnterpriseAgreedPrice ? (
          <View style={styles.planSummary}>
            <Text style={styles.planLabel}>{copy.billing.ENTERPRISE_QUOTE_LABEL}</Text>
            <Text style={styles.planValue}>{enterpriseAgreedPriceLine}</Text>
          </View>
        ) : null}

        {isOneTimeBusiness && currentBucket != null ? (
          <View style={styles.oneTimePlanSection}>
            <Text
              style={[styles.subsectionTitle, styles.oneTimeSectionTitleSpacing]}
            >
              {copy.billing.ORG_SEAT_TIER_LABEL}
            </Text>

            {upgradableBuckets.length > 0 ? (
              <>
                <View style={styles.seatTierCurrentBlock}>
                  <Text style={styles.planLabel}>
                    {copy.billing.ORG_CURRENT_SEAT_TIER_LABEL}
                  </Text>
                  <Text style={styles.planValue}>{currentSeatTierDisplay}</Text>
                </View>
                <View style={styles.seatTierUpgradeSection}>
                  <View style={styles.bucketRow}>
                    <View style={styles.bucketPickerWrap}>
                      {stripeReturnSyncing ? (
                        <View style={styles.bucketPickerWithLoader}>
                          <View
                            style={styles.bucketLoaderSlot}
                            accessibilityLabel={copy.billing.B2B_BILLING_RETURN_SYNCING_A11Y}
                          >
                            <ActivityIndicator size="small" color={theme.primary} />
                          </View>
                          <View style={styles.bucketPickerInner}>
                            <SeatBucketPicker
                              inlineRow
                              disabled
                              buckets={upgradableBuckets}
                              selectedKey={selectedBucketKey}
                              onSelect={setSelectedUpgradeBucket}
                            />
                          </View>
                        </View>
                      ) : (
                        <SeatBucketPicker
                          inlineRow
                          buckets={upgradableBuckets}
                          selectedKey={selectedBucketKey}
                          onSelect={setSelectedUpgradeBucket}
                        />
                      )}
                    </View>
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel={copy.billing.UPGRADE_SEAT_TIER}
                      disabled={busyOrg || stripeReturnSyncing}
                      onPress={handleUpgrade}
                      style={({ pressed, hovered }) => [
                        styles.upgradeBtn,
                        Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                        pressed && styles.upgradeBtnPressed,
                        hovered && Platform.OS === 'web' && styles.upgradeBtnHover,
                        (busyOrg || stripeReturnSyncing) && styles.upgradeBtnDisabled,
                      ]}
                    >
                      <Text style={styles.upgradeBtnText}>{copy.billing.UPGRADE_SEAT_TIER}</Text>
                    </Pressable>
                  </View>
                  <Pressable
                    accessibilityRole="link"
                    accessibilityLabel={copy.billing.NEED_CUSTOM_PLAN_A11Y}
                    disabled={stripeReturnSyncing}
                    onPress={() => router.push(ENTERPRISE_CONTACT_PATH as Href)}
                    style={({ pressed, hovered }) => [
                      styles.comparePlansLink,
                      stripeReturnSyncing && styles.comparePlansLinkDisabled,
                      Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                      pressed && styles.comparePlansLinkPressed,
                      hovered && Platform.OS === 'web' && styles.comparePlansLinkHover,
                    ]}
                  >
                    <Text style={styles.comparePlansLinkText}>{copy.billing.NEED_CUSTOM_PLAN}</Text>
                  </Pressable>
                </View>
              </>
            ) : (
              <Text style={styles.statusValue}>{currentSeatTierDisplay}</Text>
            )}
          </View>
        ) : null}

        {isStripeCustom && isStripeCustomPaid ? (
          <Pressable
            accessibilityRole="link"
            accessibilityLabel="Contact us to modify your plan"
            onPress={() => router.push(ENTERPRISE_CONTACT_PATH as Href)}
            style={({ pressed, hovered }) => [
              styles.manualContactLink,
              Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              pressed && styles.manualContactLinkPressed,
              hovered && Platform.OS === 'web' && styles.manualContactLinkHover,
            ]}
          >
            <Text style={styles.manualContactLinkText}>Contact us to modify your plan</Text>
          </Pressable>
        ) : null}

        {!isStripeCustom && !showSeatTierCompareWithUpgrade ? (
          <Pressable
            accessibilityRole="link"
            accessibilityLabel={copy.billing.NEED_CUSTOM_PLAN_A11Y}
            disabled={stripeReturnSyncing}
            onPress={() => router.push(ENTERPRISE_CONTACT_PATH as Href)}
            style={({ pressed, hovered }) => [
              styles.comparePlansLink,
              stripeReturnSyncing && styles.comparePlansLinkDisabled,
              Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              pressed && styles.comparePlansLinkPressed,
              hovered && Platform.OS === 'web' && styles.comparePlansLinkHover,
            ]}
          >
            <Text style={styles.comparePlansLinkText}>{copy.billing.NEED_CUSTOM_PLAN}</Text>
          </Pressable>
        ) : null}
      </View>
    </View>
  )
}
